// src/pages/wallet/WalletOverviewPage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getWalletOverview } from "../../api/walletApi";
import Button from "../../components/common/Button";
import { useAuth } from "../../hooks/useAuth";
import { STATUS_LEVEL } from "../../utils/status";
import { getProfile } from "../../api/userApi";
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiArrowUp, FiArrowDown, FiSend, FiDownload, FiClock } from "react-icons/fi";

function WalletOverviewPage() {
  const { user } = useAuth();

  // we assume user has at least user_id
  const userId = user?.user_id;

  const [wallet, setWallet] = useState(null);
  const [profile, setProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // default: New Account if profile not loaded yet
  const statusLevel = profile?.status_level || STATUS_LEVEL.NEW;
  const statusLabel = profile?.status || "NEW";

  useEffect(() => {
    if (!userId) {
      setError("User not found.");
      setLoading(false);
      return;
    }

    async function loadData() {
      try {
        setLoading(true);
        setError("");

        // Fetch profile + wallet in parallel
        // NOTE: If your getProfile() does NOT take userId, change to getProfile()
        const [profileRes, walletRes] = await Promise.all([
          getProfile(userId),
          getWalletOverview(userId),
        ]);

        setProfile(profileRes);

        // If your API returns { wallet: { ... } }, keep this.
        // If it returns the wallet object directly, change to: setWallet(walletRes);
        setWallet(walletRes.wallet || walletRes);
      } catch (err) {
        console.error(err);
        setError("Failed to load wallet overview.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [userId]);

  const formatAmount = (value) => {
    if (value == null) return "€ 0.00";
    return `€ ${Number(value).toFixed(2)}`;
  };

  const canTransferWithdraw = statusLabel >= STATUS_LEVEL.BRONZE;

  const renderStatusInfo = () => {
    if (statusLevel < STATUS_LEVEL.ACTIVATED) {
      return (
        <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiClock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 mb-1">New Account</p>
              <p className="text-amber-700">
                Pay 30€ to upgrade to <span className="font-semibold">Activated Account</span> and start earning.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (statusLevel === STATUS_LEVEL.ACTIVATED) {
      return (
        <div className="card bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-sky-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-sky-900 mb-1">Activated Account</p>
              <p className="text-sky-700">
                You can earn now. When at least 1 user activates using your refer ID, you will become <span className="font-semibold">Bronze Account</span> and unlock transfer & withdraw.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (statusLevel === STATUS_LEVEL.BRONZE) {
      return (
        <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiDollarSign className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 mb-1">Bronze Account</p>
              <p className="text-amber-700">
                You can transfer/withdraw above 5€ while always keeping at least 5€ in your wallet. Fees: 1–10€ → 1€, 11–20€ → 2€, 21–30€ → 3€, and so on.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (statusLevel === STATUS_LEVEL.SILVER) {
      return (
        <div className="card bg-gradient-to-br from-slate-50 to-gray-50 border-slate-300 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-slate-900 mb-1">Silver Account</p>
              <p className="text-slate-700">
                You can transfer/withdraw above 5€ (keeping 5€ minimum). Your transfer/withdraw fee is <span className="font-semibold">half</span> of the standard fee.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (statusLevel >= STATUS_LEVEL.GOLD) {
      return (
        <div className="card bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiTrendingUp className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900 mb-1">Gold Account</p>
              <p className="text-emerald-700">
                You can transfer/withdraw above 5€ (keeping 5€ minimum) with <span className="font-semibold">no fee</span>.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return <div>Loading wallet overview...</div>;
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
        {error}
      </div>
    );
  }

  if (!wallet) {
    return <div>No wallet data found.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="animate-fadeIn">
          <h1 className="text-3xl font-bold text-gray-900">
            Wallet Overview
          </h1>
          <p className="text-gray-600 mt-1">
            Check your balance, income breakdown, and account level.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 text-xs animate-fadeIn">
          <span className="badge badge-primary border border-primary-200">
            Account Status:{" "}
            <span className="font-semibold ml-1">{statusLevel}</span>
          </span>
        </div>
      </div>

      {/* Status info box */}
      {renderStatusInfo()}

      {/* Main balance card - featured */}
      <div className="card-gradient animate-scaleIn">
        <div className="flex items-center gap-3 mb-4">
          <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700">
            <FiDollarSign className="h-8 w-8 text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Balance</p>
            <p className="text-4xl font-bold text-gray-900">
              {formatAmount(wallet.balance)}
            </p>
          </div>
        </div>
      </div>

      {/* Income cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="card bg-gradient-to-br from-accent-50 to-green-50 border-accent-200 animate-slideUp">
          <div className="flex items-start justify-between mb-3">
            <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-accent-500 to-accent-600">
              <FiTrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-1">Review Income</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatAmount(wallet.review_income)}
          </p>
        </div>

        <div className="card bg-gradient-to-br from-secondary-50 to-purple-50 border-secondary-200 animate-slideUp" style={{animationDelay: '0.1s'}}>
          <div className="flex items-start justify-between mb-3">
            <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
              <FiTrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-1">Refer Income</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatAmount(wallet.refer_income)}
          </p>
        </div>
      </div>

      {/* Transaction cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="card hover:shadow-soft transition-all animate-slideUp" style={{animationDelay: '0.2s'}}>
          <div className="flex items-center gap-3 mb-3">
            <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
              <FiArrowDown className="h-5 w-5 text-white" />
            </div>
            <p className="text-sm text-gray-600">Received Money</p>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {formatAmount(wallet.received_money)}
          </p>
        </div>

        <div className="card hover:shadow-soft transition-all animate-slideUp" style={{animationDelay: '0.3s'}}>
          <div className="flex items-center gap-3 mb-3">
            <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600">
              <FiSend className="h-5 w-5 text-white" />
            </div>
            <p className="text-sm text-gray-600">Transferred Money</p>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {formatAmount(wallet.transfer_money)}
          </p>
        </div>

        <div className="card hover:shadow-soft transition-all animate-slideUp" style={{animationDelay: '0.4s'}}>
          <div className="flex items-center gap-3 mb-3">
            <div className="inline-flex p-2 rounded-lg bg-gradient-to-br from-red-500 to-red-600">
              <FiDownload className="h-5 w-5 text-white" />
            </div>
            <p className="text-sm text-gray-600">Withdrawn Money</p>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {formatAmount(wallet.withdraw_money)}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 animate-fadeIn">
        <Link to="/wallet/transfer">
          <Button disabled={!canTransferWithdraw} variant="primary">
            <FiSend className="inline-block mr-2 h-4 w-4" />
            Transfer Money
          </Button>
        </Link>
        <Link to="/wallet/withdraw">
          <Button
            variant="secondary"
            disabled={!canTransferWithdraw}
          >
            <FiDownload className="inline-block mr-2 h-4 w-4" />
            Withdraw Money
          </Button>
        </Link>
        <Link to="/wallet/history">
          <Button variant="outline">
            <FiClock className="inline-block mr-2 h-4 w-4" />
            View History
          </Button>
        </Link>
      </div>

      {!canTransferWithdraw && (
        <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiClock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 mb-1">Transfer & Withdraw Locked</p>
              <p className="text-amber-700">
                You can transfer or withdraw money only when your account is{" "}
                <span className="font-semibold">Bronze</span> or higher (Silver / Gold). Also, for each transfer/withdraw, you must send more than 5€ and keep at least 5€ in your wallet after fees.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletOverviewPage;
